// Native details remain usable without JavaScript. On phones all catalogue
// choices start closed; the wider layout restores its contextual defaults.
function activateCatalogDisclosures(root = document) {
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  root.querySelectorAll('[data-catalog-disclosure]').forEach((disclosure) => {
    if (!(disclosure instanceof HTMLDetailsElement) || disclosure.dataset.catalogDisclosureReady) return;
    const applyViewport = () => { disclosure.open = !mobileViewport.matches && disclosure.dataset.desktopOpen === 'true'; };
    applyViewport();
    disclosure.dataset.catalogDisclosureReady = 'true';
    mobileViewport.addEventListener('change', applyViewport);
  });
}
activateCatalogDisclosures();
document.addEventListener('shopify:section:load', (event) => activateCatalogDisclosures(event.target));

document.addEventListener('change', (event) => {
  const selector = event.target instanceof HTMLSelectElement
    ? event.target.closest('[data-language-selector]')
    : null;
  if (!selector) return;
  const form = selector.closest('form');
  if (form) form.submit();
});

document.addEventListener('toggle', (event) => {
  const navigation = event.target instanceof HTMLDetailsElement
    ? event.target.closest('.mobile-nav')
    : null;
  if (!navigation || event.target !== navigation || !navigation.open) return;

  const panel = navigation.querySelector('.mobile-nav__panel');
  if (panel) panel.scrollTop = 0;
}, true);

class PersonalizationForm extends HTMLElement {
  connectedCallback() {
    this.mainInput = this.querySelector('[data-personalization-main]');
    this.wishInput = this.querySelector('[data-personalization-wish]');
    this.dateInput = this.querySelector('[data-personalization-date]');
    this.fontSelect = this.querySelector('[data-personalization-font]');
    this.preview = this.querySelector('[data-personalization-preview]');
    this.previewMain = this.querySelector('[data-preview-main]');
    this.previewWish = this.querySelector('[data-preview-wish]');
    this.previewDate = this.querySelector('[data-preview-date]');
    this.mainCounter = this.querySelector('[data-character-count="main"]');
    this.wishCounter = this.querySelector('[data-character-count="wish"]');

    if (!this.mainInput && !this.wishInput) return;

    this.updatePreview = this.updatePreview.bind(this);
    this.mainInput?.addEventListener('input', this.updatePreview);
    this.wishInput?.addEventListener('input', this.updatePreview);
    this.dateInput?.addEventListener('input', this.updatePreview);
    this.fontSelect?.addEventListener('change', this.updatePreview);
    this.updatePreview();
  }

  disconnectedCallback() {
    this.mainInput?.removeEventListener('input', this.updatePreview);
    this.wishInput?.removeEventListener('input', this.updatePreview);
    this.dateInput?.removeEventListener('input', this.updatePreview);
    this.fontSelect?.removeEventListener('change', this.updatePreview);
  }

  updatePreview() {
    const fallback = this.preview?.dataset.fallback || 'Dein Text';
    const mainValue = this.mainInput?.value.trim() || '';
    const wishValue = this.wishInput?.value.trim() || '';
    const wishActsAsMain = !this.mainInput;

    if (this.previewMain) this.previewMain.textContent = mainValue || (wishActsAsMain && wishValue) || fallback;
    if (this.previewWish) this.previewWish.textContent = wishActsAsMain ? '' : wishValue;
    if (this.previewDate) this.previewDate.textContent = formatPersonalizationDate(this.dateInput?.value);
    if (this.preview && this.fontSelect) {
      this.preview.dataset.font = this.fontSelect.selectedOptions[0]?.dataset.font || 'modern';
    }

    updateCharacterCounter(this.mainInput, this.mainCounter);
    updateCharacterCounter(this.wishInput, this.wishCounter);
  }
}

customElements.define('personalization-form', PersonalizationForm);

class BackEngravingOption extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.toggle = this.querySelector('[data-back-engraving-toggle]');
    this.editor = this.querySelector('[data-back-engraving-editor]');
    this.textInput = this.querySelector('[data-back-engraving-text]');
    this.fontSelect = this.querySelector('[data-back-engraving-font]');
    this.propertyInput = this.querySelector('[data-back-engraving-property]');
    this.preview = this.querySelector('[data-back-engraving-preview]');
    this.previewImage = this.preview?.querySelector('img');
    this.defaultPreviewImage = this.previewImage ? { src: this.dataset.backPreviewDefaultSrc || this.previewImage.src,
      alt: 'Blanko-Rückseite', width: Number(this.dataset.backPreviewDefaultWidth || this.previewImage.width),
      height: Number(this.dataset.backPreviewDefaultHeight || this.previewImage.height) } : null;
    this.previewText = this.querySelector('[data-back-engraving-preview-text]');
    this.counter = this.querySelector('[data-back-character-count]');
    this.variantSelect = this.form?.querySelector('[data-variant-select]');

    if (!this.toggle || !this.variantSelect) return;

    this.handleToggle = this.handleToggle.bind(this);
    this.handlePreviewInput = this.handlePreviewInput.bind(this);
    this.syncFromVariant = this.syncFromVariant.bind(this);
    this.fitPreviewText = this.fitPreviewText.bind(this);

    this.toggle.addEventListener('change', this.handleToggle);
    this.textInput?.addEventListener('input', this.handlePreviewInput);
    this.fontSelect?.addEventListener('change', this.handlePreviewInput);
    this.variantSelect.addEventListener('change', this.syncFromVariant);
    this.previewImage?.addEventListener('load', this.fitPreviewText);
    if (this.preview && 'ResizeObserver' in window) {
      this.previewResizeObserver = new ResizeObserver(this.fitPreviewText);
      this.previewResizeObserver.observe(this.preview);
    }
    document.fonts?.ready.then(() => { if (this.isConnected) this.fitPreviewText(); });
    this.syncFromVariant();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.handleToggle);
    this.textInput?.removeEventListener('input', this.handlePreviewInput);
    this.fontSelect?.removeEventListener('change', this.handlePreviewInput);
    this.variantSelect?.removeEventListener('change', this.syncFromVariant);
    this.previewImage?.removeEventListener('load', this.fitPreviewText);
    this.previewResizeObserver?.disconnect();
    this.mainPreview?.remove();
  }

  get selectedVariant() {
    return this.variantSelect?.selectedOptions[0];
  }

  handleToggle() {
    const requestedState = this.toggle.checked;
    const currentVariant = this.selectedVariant;
    const group = currentVariant?.dataset.backGroup || '';
    const targetVariant = [...this.variantSelect.options].find(
      (option) =>
        option.dataset.backGroup === group &&
        option.dataset.backEngraving === String(requestedState) &&
        !option.disabled,
    );

    if (!targetVariant) {
      this.toggle.checked = currentVariant?.dataset.backEngraving === 'true';
      this.toggle.setCustomValidity('Diese Auswahl ist für die gewählte Variante nicht verfügbar.');
      this.toggle.reportValidity();
      this.toggle.setCustomValidity('');
      this.updateState();
      return;
    }

    this.variantSelect.value = targetVariant.value;
    this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncFromVariant() {
    if (!this.toggle) return;
    this.toggle.checked = this.selectedVariant?.dataset.backEngraving === 'true';
    this.syncPreviewImage();
    this.updateState();
  }

  syncPreviewImage() {
    if (!this.previewImage || !this.defaultPreviewImage) return;
    const data = this.selectedVariant?.dataset;
    const mapped = Boolean(data?.backPreviewSrc);
    const src = mapped ? data.backPreviewSrc : this.defaultPreviewImage.src;
    const alt = mapped ? data.backPreviewAlt : this.defaultPreviewImage.alt;
    if (this.previewImage.src !== src) setProductGalleryImageSource(this.previewImage, src, alt);
    this.previewImage.alt = alt;
    this.previewImage.width = Number(mapped ? data.backPreviewWidth : this.defaultPreviewImage.width);
    this.previewImage.height = Number(mapped ? data.backPreviewHeight : this.defaultPreviewImage.height);
  }

  updateState() {
    const enabled = Boolean(this.toggle?.checked);
    if (this.editor) this.editor.hidden = !enabled;
    if (this.textInput) {
      this.textInput.disabled = !enabled;
      this.textInput.required = enabled;
      this.textInput.setAttribute('aria-required', String(enabled));
    }
    if (this.fontSelect) this.fontSelect.disabled = !enabled;
    if (this.propertyInput) {
      const surcharge = Number(this.dataset.surchargeCents || 290) / 100;
      const formattedSurcharge = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(surcharge);
      this.propertyInput.value = enabled ? `Ja (+${formattedSurcharge})` : 'Nein';
    }
    this.handlePreviewInput();
  }

  validateBackText() {
    if (!this.textInput) return;
    const hasSymbols = /\p{S}|\p{Extended_Pictographic}|\u200d|\ufe0f|\u20e3/u.test(this.textInput.value);
    this.textInput.setCustomValidity(!this.textInput.disabled && hasSymbols
      ? 'Bitte verwende nur Text. Symbole und Emojis werden nicht mitgraviert.' : '');
  }

  handlePreviewInput() {
    this.validateBackText();
    const value = this.textInput?.value.trim() || '';
    if (this.previewText) this.previewText.textContent = value || 'Dein Spruch';
    if (this.preview && this.fontSelect) {
      this.preview.dataset.font = this.fontSelect.selectedOptions[0]?.dataset.font || 'script';
    }
    updateCharacterCounter(this.textInput, this.counter);
    this.fitPreviewText();
    this.syncMainPreview();
  }

  syncMainPreview() {
    const gallery = this.closest('[data-product-section]')?.querySelector('[data-product-gallery]');
    const stage = gallery?.querySelector('[data-product-gallery-stage]');
    if (!stage || gallery.dataset.supplementalProductGallery !== 'true' || !this.previewImage) return;
    const value = this.textInput?.value.trim() || '';
    if (!this.toggle?.checked || !value) {
      if (this.mainPreview && !this.mainPreview.hidden) {
        this.mainPreview.hidden = true;
        gallery.querySelector('[data-product-gallery-thumb]')?.click();
      }
      return;
    }
    if (!this.mainPreview) {
      this.mainPreview = this.preview.cloneNode(true);
      this.mainPreview.classList.add('back-engraving__preview--main');
      this.mainPreview.setAttribute('aria-label', 'Deine Rückseitengravur');
      stage.appendChild(this.mainPreview);
      this.mainPreview.querySelector('img')?.addEventListener('load', this.fitPreviewText);
      this.previewResizeObserver?.observe(this.mainPreview);
    }
    const image = this.mainPreview.querySelector('img');
    if (image && image.src !== this.previewImage.src) setProductGalleryImageSource(image, this.previewImage.src, 'Deine Rückseitengravur');
    this.mainPreview.querySelector('[data-back-engraving-preview-text]').textContent = value;
    this.mainPreview.querySelector('small').textContent = 'Deine Rückseitengravur';
    this.mainPreview.dataset.font = this.preview.dataset.font;
    delete gallery.dataset.personalizationPreviewPaused;
    delete gallery.dataset.gallerySelectedSrc;
    delete gallery.dataset.gallerySelectedAlt;
    this.mainPreview.hidden = false;
    gallery.classList.add('is-personalizing');
    gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((thumb) => {
      thumb.classList.remove('is-active');
      thumb.setAttribute('aria-pressed', 'false');
    });
    const counter = gallery.querySelector('[data-product-gallery-counter]');
    if (counter) counter.textContent = 'Deine Rückseitengravur';
    this.fitPreviewText();
  }

  fitPreviewText() {
    if (!this.previewText || !this.previewImage || this.editor?.hidden) return;
    const imageHeight = this.previewImage.getBoundingClientRect().height;
    if (!imageHeight) return;
    this.previewText.style.fontSize = '';
    let size = parseFloat(window.getComputedStyle(this.previewText).fontSize);
    const maxHeight = imageHeight * 0.4;
    while (this.previewText.getBoundingClientRect().height > maxHeight && size > 10) {
      size -= 1;
      this.previewText.style.fontSize = `${size}px`;
    }
    const mainText = this.mainPreview?.querySelector('[data-back-engraving-preview-text]');
    if (mainText && !this.mainPreview.hidden) {
      mainText.style.fontSize = '';
      let mainSize = parseFloat(window.getComputedStyle(mainText).fontSize);
      const availableHeight = this.mainPreview.getBoundingClientRect().height * 0.4;
      while (mainText.getBoundingClientRect().height > availableHeight && mainSize > 10) {
        mainSize -= 1;
        mainText.style.fontSize = `${mainSize}px`;
      }
    }
  }
}

customElements.define('back-engraving-option', BackEngravingOption);

class WoodBaseEngravingOption extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.toggle = this.querySelector('[data-wood-base-engraving-toggle]');
    this.editor = this.querySelector('[data-wood-base-engraving-editor]');
    this.textInput = this.querySelector('[data-wood-base-engraving-text]');
    this.fontSelect = this.querySelector('[data-wood-base-engraving-font]');
    this.propertyInput = this.querySelector('[data-wood-base-engraving-property]');
    this.counter = this.querySelector('[data-wood-base-character-count]');
    this.variantSelect = this.form?.querySelector('[data-variant-select]');
    this.productSection = this.closest('[data-product-section]');
    this.mainPreview = this.productSection?.querySelector('[data-wood-base-image-preview]');
    this.mainPreviewText = this.mainPreview?.querySelector('[data-wood-base-image-preview-text]');
    this.productImage = this.productSection?.querySelector('[data-product-gallery-stage] .product-gallery__stage-image');
    this.previewSurface = this.mainPreview?.parentElement;

    if (!this.toggle || !this.variantSelect) return;

    this.handleToggle = this.handleToggle.bind(this);
    this.handlePreviewInput = this.handlePreviewInput.bind(this);
    this.syncFromVariant = this.syncFromVariant.bind(this);
    this.positionMainPreview = this.positionMainPreview.bind(this);
    this.syncEmbeddedPreview = this.syncEmbeddedPreview.bind(this);

    this.toggle.addEventListener('change', this.handleToggle);
    this.textInput?.addEventListener('input', this.handlePreviewInput);
    this.fontSelect?.addEventListener('change', this.handlePreviewInput);
    this.variantSelect.addEventListener('change', this.syncFromVariant);
    this.productImage?.addEventListener('load', this.positionMainPreview);

    if (this.productImage && 'MutationObserver' in window) {
      this.imageObserver = new MutationObserver(this.positionMainPreview);
      this.imageObserver.observe(this.productImage, {
        attributes: true,
        attributeFilter: ['src', 'srcset'],
      });
    }

    if (this.mainPreview?.parentElement && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.positionMainPreview);
      this.resizeObserver.observe(this.mainPreview.parentElement);
    }

    if (this.previewSurface && 'MutationObserver' in window) {
      this.previewObserver = new MutationObserver(this.syncEmbeddedPreview);
      this.previewObserver.observe(this.previewSurface, { childList: true });
    }

    this.syncFromVariant();
    this.positionMainPreview();
    this.syncEmbeddedPreview();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('change', this.handleToggle);
    this.textInput?.removeEventListener('input', this.handlePreviewInput);
    this.fontSelect?.removeEventListener('change', this.handlePreviewInput);
    this.variantSelect?.removeEventListener('change', this.syncFromVariant);
    this.productImage?.removeEventListener('load', this.positionMainPreview);
    this.imageObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.previewObserver?.disconnect();
  }

  get selectedVariant() {
    return this.variantSelect?.selectedOptions[0];
  }

  handleToggle() {
    const requestedState = this.toggle.checked;
    const currentVariant = this.selectedVariant;
    const group = currentVariant?.dataset.woodBaseGroup || '';
    const targetVariant = [...this.variantSelect.options].find(
      (option) =>
        option.dataset.woodBaseGroup === group &&
        option.dataset.woodBaseEngraving === String(requestedState) &&
        !option.disabled,
    );

    if (!targetVariant) {
      this.toggle.checked = currentVariant?.dataset.woodBaseEngraving === 'true';
      this.toggle.setCustomValidity('Diese Auswahl ist für die gewählte Variante nicht verfügbar.');
      this.toggle.reportValidity();
      this.toggle.setCustomValidity('');
      this.updateState();
      return;
    }

    this.variantSelect.value = targetVariant.value;
    this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncFromVariant() {
    if (!this.toggle) return;
    this.toggle.checked = this.selectedVariant?.dataset.woodBaseEngraving === 'true';
    this.updateState();
  }

  updateState() {
    const enabled = Boolean(this.toggle?.checked);
    if (this.editor) this.editor.hidden = !enabled;
    if (this.textInput) {
      this.textInput.disabled = !enabled;
      this.textInput.required = enabled;
      this.textInput.setAttribute('aria-required', String(enabled));
    }
    if (this.fontSelect) this.fontSelect.disabled = !enabled;
    if (this.mainPreview) {
      this.mainPreview.hidden = !enabled;
      this.mainPreview.setAttribute('aria-hidden', String(!enabled));
    }
    if (this.propertyInput) {
      const surcharge = Number(this.dataset.surchargeCents || 499) / 100;
      const formattedSurcharge = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(surcharge);
      this.propertyInput.value = enabled ? `Ja (+${formattedSurcharge})` : 'Nein';
    }
    this.handlePreviewInput();
    this.syncEmbeddedPreview();
  }

  handlePreviewInput() {
    const value = this.textInput?.value.trim() || '';
    const enabled = Boolean(this.toggle?.checked);
    const hasValue = value.length > 0;
    if (this.mainPreviewText) this.mainPreviewText.textContent = value;
    if (this.mainPreview) {
      this.mainPreview.hidden = !enabled || !hasValue;
      this.mainPreview.setAttribute('aria-hidden', String(!enabled || !hasValue));
    }
    if (this.mainPreview && this.fontSelect) {
      this.mainPreview.dataset.font = this.fontSelect.selectedOptions[0]?.dataset.font || 'script';
    }
    updateCharacterCounter(this.textInput, this.counter);
    this.fitMainPreviewText();
    this.syncEmbeddedPreview();
    this.ensurePreviewFontLoaded();
  }

  ensurePreviewFontLoaded() {
    if (!document.fonts || !this.mainPreview) return;
    const fontKey = this.mainPreview.dataset.font || 'script';
    const fontFamily = {
      modern: 'PersonalizePilot Poppins',
      serif: 'PersonalizePilot Playfair Display',
      script: 'PersonalizePilot Dancing Script',
      bold: 'PersonalizePilot Bebas Neue',
    }[fontKey] || 'PersonalizePilot Dancing Script';
    if (this.previewFontLoadKey === fontFamily) return;
    this.previewFontLoadKey = fontFamily;
    document.fonts
      .load(`16px "${fontFamily}"`)
      .then(() => {
        if (this.previewFontLoadKey !== fontFamily) return;
        window.requestAnimationFrame(() => {
          this.fitMainPreviewText();
          this.syncEmbeddedPreview();
        });
      })
      .catch(() => {
        if (this.previewFontLoadKey === fontFamily) this.previewFontLoadKey = '';
      });
  }

  positionMainPreview() {
    if (!this.mainPreview || !this.productImage) return;

    const surface = this.mainPreview.parentElement;
    const surfaceRect = surface?.getBoundingClientRect();
    const imageRect = this.productImage.getBoundingClientRect();
    const naturalWidth = this.productImage.naturalWidth;
    const naturalHeight = this.productImage.naturalHeight;

    if (
      !surfaceRect ||
      imageRect.width <= 1 ||
      imageRect.height <= 1 ||
      naturalWidth <= 0 ||
      naturalHeight <= 0
    ) {
      return;
    }

    const imageAspect = naturalWidth / naturalHeight;
    const elementAspect = imageRect.width / imageRect.height;
    let contentWidth = imageRect.width;
    let contentHeight = imageRect.height;
    let contentLeft = imageRect.left;
    let contentTop = imageRect.top;

    if (imageAspect > elementAspect) {
      contentHeight = contentWidth / imageAspect;
      contentTop += (imageRect.height - contentHeight) / 2;
    } else {
      contentWidth = contentHeight * imageAspect;
      contentLeft += (imageRect.width - contentWidth) / 2;
    }

    this.mainPreview.style.left = `${contentLeft - surfaceRect.left}px`;
    this.mainPreview.style.top = `${contentTop - surfaceRect.top}px`;
    this.mainPreview.style.width = `${contentWidth}px`;
    this.mainPreview.style.height = `${contentHeight}px`;
    this.mainPreview.dataset.imageShape = imageAspect < 0.88 ? 'tall' : 'square';
    this.positionPreviewEngraving(this.mainPreview);
    this.fitMainPreviewText();
    this.syncEmbeddedPreview();
  }

  fitMainPreviewText() {
    this.fitPreviewText(this.mainPreview, this.mainPreviewText);
    this.fitPreviewText(
      this.embeddedPreview,
      this.embeddedPreview?.querySelector('[data-wood-base-image-preview-text]'),
    );
  }

  positionPreviewEngraving(preview) {
    if (!preview) return;
    const engraving = preview.querySelector('.wood-base-image-preview__engraving');
    if (!engraving) return;
    const fallback =
      preview.dataset.imageShape === 'tall'
        ? { x: 49.5, y: 77.4, width: 55, height: 7.2 }
        : { x: 50, y: 81.2, width: 54, height: 7.4 };
    let configured = null;
    try {
      configured = JSON.parse(preview.dataset.previewArea || 'null');
    } catch {
      configured = null;
    }
    const area = ['x', 'y', 'width', 'height'].every(
      (key) => Number.isFinite(Number(configured?.[key])),
    )
      ? {
          x: Math.max(0, Math.min(100, Number(configured.x))),
          y: Math.max(0, Math.min(100, Number(configured.y))),
          width: Math.max(1, Math.min(100, Number(configured.width))),
          height: Math.max(1, Math.min(100, Number(configured.height))),
        }
      : fallback;
    const width = Math.min(area.width, 2 * Math.min(area.x, 100 - area.x));
    const height = Math.min(area.height, 2 * Math.min(area.y, 100 - area.y));
    engraving.style.left = `${area.x - width / 2}%`;
    engraving.style.top = `${area.y - height / 2}%`;
    engraving.style.width = `${width}%`;
    engraving.style.height = `${height}%`;
  }

  fitPreviewText(preview, previewText) {
    if (!previewText || preview?.hidden) return;
    const slot = previewText.parentElement;
    if (!slot || slot.clientWidth <= 1 || slot.clientHeight <= 1) return;

    const slotStyles = window.getComputedStyle(slot);
    const padding = (property) => Math.max(0, parseFloat(slotStyles[property]) || 0);
    const contentWidth = slot.clientWidth - padding('paddingLeft') - padding('paddingRight');
    const contentHeight = slot.clientHeight - padding('paddingTop') - padding('paddingBottom');
    if (contentWidth <= 1 || contentHeight <= 1) return;

    const textLength = Math.max(1, Array.from(previewText.textContent || '').length);
    const heightLimit = contentHeight * 0.78;
    const availableWidth = contentWidth * 0.96;
    const availableHeight = contentHeight * 0.9;
    const estimatedSingleLineFit = availableWidth / (textLength * 0.52);
    let size = Math.min(heightLimit, estimatedSingleLineFit);
    previewText.style.fontSize = `${size}px`;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      const measuredWidth = previewText.scrollWidth;
      const measuredHeight = previewText.scrollHeight;
      if (measuredWidth <= availableWidth && measuredHeight <= availableHeight) break;
      const scale = Math.min(
        availableWidth / Math.max(1, measuredWidth),
        availableHeight / Math.max(1, measuredHeight),
        0.96,
      );
      size *= scale;
      previewText.style.fontSize = `${size}px`;
    }
  }

  syncEmbeddedPreview() {
    if (!this.previewSurface || !this.mainPreview) return;

    const personalizePilotPreview = this.previewSurface.querySelector('.personalize-pilot-main-preview');
    if (!personalizePilotPreview) {
      this.embeddedPreview = null;
      return;
    }

    let embeddedPreview = personalizePilotPreview.querySelector(
      '[data-wood-base-embedded-preview]',
    );
    if (!embeddedPreview) {
      embeddedPreview = document.createElement('div');
      embeddedPreview.className = 'wood-base-image-preview wood-base-image-preview--embedded';
      embeddedPreview.dataset.woodBaseEmbeddedPreview = '';
      embeddedPreview.setAttribute('aria-hidden', 'true');
      embeddedPreview.innerHTML =
        '<span class="wood-base-image-preview__engraving">' +
        '<span data-wood-base-image-preview-text></span>' +
        '</span>';
      personalizePilotPreview.appendChild(embeddedPreview);
    }

    this.embeddedPreview = embeddedPreview;
    const enabled = Boolean(this.toggle?.checked);
    const embeddedText = embeddedPreview.querySelector(
      '[data-wood-base-image-preview-text]',
    );
    const value = this.textInput?.value.trim() || '';
    const hasValue = value.length > 0;
    embeddedPreview.hidden = !enabled || !hasValue;
    embeddedPreview.setAttribute('aria-hidden', String(!enabled || !hasValue));
    embeddedPreview.dataset.font = this.mainPreview.dataset.font || 'script';
    embeddedPreview.dataset.imageShape = this.mainPreview.dataset.imageShape || 'square';
    embeddedPreview.dataset.previewArea = this.mainPreview.dataset.previewArea || '';
    if (embeddedText) embeddedText.textContent = value;

    window.requestAnimationFrame(() => {
      this.positionPreviewEngraving(embeddedPreview);
      this.fitPreviewText(embeddedPreview, embeddedText);
    });
  }
}

customElements.define('wood-base-engraving-option', WoodBaseEngravingOption);

function attachWoodEngravingEffect(widget) {
  if (!widget || widget.dataset.woodEngravingEffectAttached === 'true') return;
  widget.dataset.woodEngravingEffectAttached = 'true';

  let experienceSettings = {};
  try {
    const parsedExperienceSettings = JSON.parse(
      widget.dataset.experienceSettings || '{}',
    );
    experienceSettings =
      parsedExperienceSettings &&
      typeof parsedExperienceSettings === 'object' &&
      !Array.isArray(parsedExperienceSettings)
        ? parsedExperienceSettings
        : {};
  } catch {
    experienceSettings = {};
  }

  const materialInput = widget.querySelector('[data-material-input]');
  const syncEffect = () => {
    const profiles = Array.isArray(experienceSettings.materialProfiles)
      ? experienceSettings.materialProfiles
      : [];
    const selectedMaterial = materialInput?.value || '';
    const profile =
      profiles.find((item) => item.materialValue === selectedMaterial) ||
      profiles.find((item) => item.id === experienceSettings.defaultMaterialProfileId) ||
      profiles[0];
    const productTitle = widget.dataset.productTitle || '';
    const woodProduct =
      profile?.kind === 'wood' ||
      /holz|wood|bamboo|kork|erinnerungsbox|geldhalter/i.test(productTitle);
    const effect = profile?.engravingEffect || (woodProduct ? 'engraved' : 'none');
    const materialKind = woodProduct ? 'wood' : profile?.kind || 'other';
    const previewSurface = widget.closest('[data-product-section]') || document;

    [
      widget.querySelector('.personalize-pilot-widget__mockup'),
      previewSurface.querySelector('.personalize-pilot-main-preview'),
    ]
      .filter(Boolean)
      .forEach((root) => {
        root.dataset.materialKind = materialKind;
        root.dataset.engravingEffect = effect;
      });
  };

  materialInput?.addEventListener('change', syncEffect);
  const section = widget.closest('[data-product-section]');
  if (section && 'MutationObserver' in window) {
    const observer = new MutationObserver(syncEffect);
    observer.observe(section, { childList: true, subtree: true });
  }
  syncEffect();
}

function attachWoodEngravingEffects() {
  document
    .querySelectorAll('[data-personalize-pilot-widget]')
    .forEach(attachWoodEngravingEffect);
}

attachWoodEngravingEffects();
document.addEventListener('shopify:section:load', attachWoodEngravingEffects);

function updateCharacterCounter(input, counter) {
  if (!input || !counter) return;
  counter.textContent = `${input.value.length} / ${input.maxLength}`;
}

function formatPersonalizationDate(value) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('de-DE').format(date);
}

class PersonalizationStudio extends HTMLElement {
  connectedCallback() {
    this.productSelect = this.querySelector('[data-studio-product]');
    this.preview = this.querySelector('[data-studio-preview]');
    this.previewImage = this.querySelector('[data-studio-image]');
    this.previewMain = this.querySelector('[data-studio-preview-main]');
    this.previewWish = this.querySelector('[data-studio-preview-wish]');
    this.previewDate = this.querySelector('[data-studio-preview-date]');
    this.mainInput = this.querySelector('[data-studio-input="main"]');
    this.wishInput = this.querySelector('[data-studio-input="wish"]');
    this.dateInput = this.querySelector('[data-studio-input="date"]');
    this.motifInput = this.querySelector('[data-studio-input="motif"]');
    this.fontSelect = this.querySelector('[data-studio-input="font"]');
    this.mainLabel = this.querySelector('[data-studio-main-label]');
    this.counter = this.querySelector('[data-studio-counter]');
    this.summaryInput = this.querySelector('[data-studio-summary]');
    this.choices = [...this.querySelectorAll('[data-profile-choice]')];

    if (!this.productSelect || !this.preview || !this.mainInput) return;

    this.handleProductChange = this.handleProductChange.bind(this);
    this.handleInput = this.handleInput.bind(this);
    this.handleChoice = this.handleChoice.bind(this);

    this.productSelect.addEventListener('change', this.handleProductChange);
    this.querySelectorAll('[data-studio-input]').forEach((input) => {
      input.addEventListener(input.tagName === 'SELECT' ? 'change' : 'input', this.handleInput);
    });
    this.choices.forEach((choice) => choice.addEventListener('click', this.handleChoice));
    this.handleProductChange();
  }

  disconnectedCallback() {
    this.productSelect?.removeEventListener('change', this.handleProductChange);
    this.querySelectorAll('[data-studio-input]').forEach((input) => {
      input.removeEventListener(input.tagName === 'SELECT' ? 'change' : 'input', this.handleInput);
    });
    this.choices?.forEach((choice) => choice.removeEventListener('click', this.handleChoice));
  }

  get activeOption() {
    return this.productSelect?.selectedOptions[0];
  }

  handleChoice(event) {
    this.selectProfile(event.currentTarget.dataset.profileChoice);
  }

  selectProfile(profile) {
    const option = [...this.productSelect.options].find((candidate) => candidate.dataset.profile === profile);
    if (!option) return;
    this.productSelect.value = option.value;
    this.handleProductChange();
  }

  handleProductChange() {
    const option = this.activeOption;
    if (!option) return;

    const profile = option.dataset.profile || 'acrylic_sign';
    this.preview.dataset.profile = profile;
    if (this.previewImage && option.dataset.image) {
      this.previewImage.src = option.dataset.image;
      this.previewImage.alt = option.dataset.imageAlt || option.textContent.trim();
    }

    this.mainLabel.textContent = option.dataset.mainLabel || 'Name oder Gravurtext';
    this.mainInput.placeholder = option.dataset.mainPlaceholder || '';
    this.mainInput.maxLength = Number(option.dataset.max || 40);

    this.setConditionalField('wish', option.dataset.showWish === 'true');
    this.setConditionalField('date', option.dataset.showDate === 'true');
    this.setConditionalField('motif', option.dataset.showMotif === 'true');
    this.choices.forEach((choice) => {
      choice.setAttribute('aria-pressed', String(choice.dataset.profileChoice === profile));
    });

    this.handleInput();
  }

  setConditionalField(name, isVisible) {
    const wrapper = this.querySelector(`[data-studio-conditional="${name}"]`);
    if (!wrapper) return;
    wrapper.hidden = !isVisible;
    wrapper.querySelectorAll('input, textarea, select').forEach((control) => {
      control.disabled = !isVisible;
    });
  }

  handleInput() {
    const option = this.activeOption;
    if (!option) return;

    const mainValue = this.mainInput.value.trim();
    const wishValue = this.wishInput?.disabled ? '' : this.wishInput?.value.trim() || '';
    const dateValue = this.dateInput?.disabled ? '' : this.dateInput?.value || '';
    const font = this.fontSelect?.selectedOptions[0]?.dataset.font || 'modern';

    this.preview.dataset.font = font;
    this.previewMain.textContent = mainValue || option.dataset.preview || 'Dein Text';
    this.previewWish.textContent = wishValue;
    this.previewDate.textContent = formatPersonalizationDate(dateValue);
    updateCharacterCounter(this.mainInput, this.counter);

    if (this.summaryInput) {
      const details = [
        `Artikel: ${option.value}`,
        `Gravurtext: ${mainValue}`,
        wishValue && `Wunschtext: ${wishValue}`,
        dateValue && `Datum: ${dateValue}`,
        !this.motifInput?.disabled && this.motifInput?.value.trim() && `Motiv: ${this.motifInput.value.trim()}`,
        `Schriftstil: ${this.fontSelect?.value || ''}`,
      ].filter(Boolean);
      this.summaryInput.value = details.join(' | ');
    }
  }
}

customElements.define('personalization-studio', PersonalizationStudio);

class VariantAddonControls extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.variantSelect = this.form?.querySelector('[data-variant-select]');
    this.buttons = [...this.querySelectorAll('[data-variant-addon-toggle]')];
    this.status = this.querySelector('[data-variant-addon-status]');
    this.shippingBonus = this.querySelector('[data-service-shipping-bonus]');

    this.querySelectorAll('.addon-information').forEach((info) => {
      if (info.dataset.ready) return;
      info.dataset.ready = 'true';
      let openedByHover = false;
      info.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' && !info.open) {
          openedByHover = true;
          info.open = true;
        }
      });
      info.querySelector('summary').addEventListener('click', (event) => {
        // A mouse click following hover must not immediately hide the explanation.
        if (openedByHover && event.detail > 0) event.preventDefault();
        openedByHover = false;
      });
      info.closest('.variant-addon-row').addEventListener('pointerleave', () => {
        if (!info.contains(document.activeElement)) {
          info.open = false;
          openedByHover = false;
        }
      });
      info.addEventListener('focusout', (event) => {
        if (!info.contains(event.relatedTarget)) {
          info.open = false;
          openedByHover = false;
        }
      });
      info.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          info.open = false;
          openedByHover = false;
          event.stopPropagation();
        }
      });
    });

    if (!this.variantSelect || this.buttons.length === 0) return;

    this.handleClick = this.handleClick.bind(this);
    this.syncFromVariant = this.syncFromVariant.bind(this);
    this.buttons.forEach((button) => button.addEventListener('click', this.handleClick));
    this.variantSelect.addEventListener('change', this.syncFromVariant);
    this.hidden = false;
    this.form.classList.add('variant-addons-ready');
    this.syncFromVariant();
  }

  disconnectedCallback() {
    this.buttons?.forEach((button) => button.removeEventListener('click', this.handleClick));
    this.variantSelect?.removeEventListener('change', this.syncFromVariant);
  }

  get selectedVariant() {
    return this.variantSelect?.selectedOptions[0];
  }

  optionValues(option) {
    try {
      const values = JSON.parse(option?.dataset.options || '[]');
      return Array.isArray(values) ? values.map(String) : [];
    } catch {
      return [];
    }
  }

  handleClick(event) {
    const button = event.currentTarget;
    const optionIndex = Number(button.dataset.optionIndex);
    const nextValue =
      button.getAttribute('aria-pressed') === 'true'
        ? button.dataset.offValue
        : button.dataset.onValue;
    const selectedValues = this.optionValues(this.selectedVariant);

    if (!Number.isInteger(optionIndex) || !nextValue || selectedValues.length === 0) return;
    selectedValues[optionIndex] = nextValue;

    const targetVariant = [...this.variantSelect.options].find((option) => {
      const values = this.optionValues(option);
      return (
        !option.disabled &&
        values.length === selectedValues.length &&
        values.every((value, index) => value === selectedValues[index])
      );
    });

    if (!targetVariant) {
      if (this.status) this.status.textContent = this.dataset.unavailableMessage || '';
      this.syncFromVariant();
      return;
    }

    if (this.status) this.status.textContent = '';
    this.variantSelect.value = targetVariant.value;
    this.variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  syncFromVariant() {
    const selectedValues = this.optionValues(this.selectedVariant);
    this.buttons.forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const isActive = selectedValues[optionIndex] === button.dataset.onValue;
      button.setAttribute('aria-pressed', String(isActive));
      button.classList.toggle('is-active', isActive);
    });
    this.syncShippingBonus(selectedValues);
  }

  syncShippingBonus(selectedValues) {
    if (!this.shippingBonus) return;
    const bonus = this.shippingBonus;
    const checked = selectedValues[Number(bonus.dataset.checkIndex)] === bonus.dataset.checkValue;
    const expressed = selectedValues[Number(bonus.dataset.expressIndex)] === bonus.dataset.expressValue;
    const complete = checked && expressed;
    const title = complete ? bonus.dataset.activeTitle : bonus.dataset.title;
    const description = complete ? bonus.dataset.activeDescription : checked ? bonus.dataset.missingExpress : expressed ? bonus.dataset.missingCheck : bonus.dataset.description;
    bonus.dataset.complete = String(complete);
    const titleNode = bonus.querySelector('[data-bonus-title]');
    const descriptionNode = bonus.querySelector('[data-bonus-description]');
    // Do not repeatedly replace live-region text when the variant is unchanged.
    if (titleNode && titleNode.textContent !== title) titleNode.textContent = title;
    if (descriptionNode && descriptionNode.textContent !== description) descriptionNode.textContent = description;
  }
}

customElements.define('variant-addon-controls', VariantAddonControls);

function updateSelectedVariant(select) {
    const option = select.selectedOptions[0];
    const form = select.closest('form');
    const button = form?.querySelector('[data-add-to-cart]');
    const section = form?.closest('[data-product-section]');
    const price = section?.querySelector('[data-price] .price__current');
    const available = option?.dataset.available === 'true';

    if (button) {
      button.disabled = !available;
      button.querySelector('span').textContent = available
        ? button.dataset.availableLabel
        : button.dataset.soldOutLabel;
    }

    if (price && option?.dataset.price) price.textContent = option.dataset.price;

    const mediaId = option?.dataset.variantMediaId;
    const thumbnail = mediaId && Array.from(section?.querySelectorAll('[data-product-gallery-thumb]') || [])
      .find((candidate) => candidate.dataset.galleryMediaId === mediaId);
    if (thumbnail && thumbnail.getAttribute('aria-pressed') !== 'true') {
      selectProductGalleryImage(thumbnail);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', select.value);
    window.history.replaceState({}, '', url);
}

function pausePersonalizationPreviewForGallery(gallery) {
  const preview = gallery.querySelector('.personalize-pilot-main-preview');
  if (preview) {
    preview.hidden = true;
    preview.classList.remove('is-active');
  }
  const reversePreview = gallery.querySelector('.back-engraving__preview--main');
  if (reversePreview) reversePreview.hidden = true;

  gallery.classList.remove('is-personalizing');
  gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((thumb) => {
    thumb.setAttribute('aria-disabled', 'false');
  });

  const previewStatus = gallery.querySelector('[data-personalize-pilot-preview-status]');
  if (previewStatus) {
    previewStatus.hidden = true;
    previewStatus.textContent = '';
  }
}

function setProductGalleryImageSource(image, src, alt) {
  if (!image || !src) return;

  image.src = src;
  image.srcset = src;
  image.sizes = '100vw';
  image.alt = alt || image.alt;
  image.closest('picture')?.querySelectorAll('source').forEach((source) => {
    source.srcset = src;
    source.sizes = '100vw';
  });
}

function selectProductGalleryImage(button) {
  const gallery = button.closest('[data-product-gallery]');
  const stage = gallery?.querySelector('[data-product-gallery-stage]');
  const image = stage?.querySelector('img');
  const nextSrc = button.dataset.galleryImageSrc;

  if (!gallery || !stage || !image || !nextSrc) return;

  gallery.dataset.personalizationPreviewPaused = 'true';
  gallery.dataset.gallerySelectedSrc = nextSrc;
  gallery.dataset.gallerySelectedAlt = button.dataset.galleryImageAlt || image.alt;
  pausePersonalizationPreviewForGallery(gallery);

  gallery.classList.add('is-changing');
  setProductGalleryImageSource(image, nextSrc, button.dataset.galleryImageAlt);

  const dialogImage = gallery.querySelector('[data-product-gallery-dialog-image]');
  if (dialogImage) {
    setProductGalleryImageSource(
      dialogImage,
      button.dataset.galleryDialogSrc || nextSrc,
      button.dataset.galleryImageAlt || image.alt,
    );
  }

  gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((thumb) => {
    const isActive = thumb === button;
    thumb.classList.toggle('is-active', isActive);
    thumb.setAttribute('aria-pressed', String(isActive));
  });

  const counter = gallery.querySelector('[data-product-gallery-counter]');
  const imageCount = gallery.querySelectorAll('[data-product-gallery-thumb]').length;
  const imageIndex = Number(button.dataset.galleryIndex || 1);
  if (counter) counter.textContent = `Bild ${imageIndex} von ${imageCount}`;

  button.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    block: 'nearest',
    inline: 'nearest',
  });

  window.setTimeout(() => gallery.classList.remove('is-changing'), 180);
}

function stepProductGallery(button) {
  const gallery = button.closest('[data-product-gallery]');
  const thumbnails = Array.from(gallery?.querySelectorAll('[data-product-gallery-thumb]') || []);
  if (!gallery || thumbnails.length < 2) return;

  const activeIndex = Math.max(0, thumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-pressed') === 'true'));
  const direction = Number(button.dataset.productGalleryDirection || 1);
  const nextIndex = (activeIndex + direction + thumbnails.length) % thumbnails.length;
  selectProductGalleryImage(thumbnails[nextIndex]);
}

function activateProductGalleryNavigation(root = document) {
  root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    if (gallery.dataset.galleryNavigationReady === 'true') return;

    const keepGalleryBrowsingState = () => {
      gallery.querySelectorAll('[data-product-gallery-thumb][aria-disabled="true"]').forEach((thumbnail) => {
        thumbnail.setAttribute('aria-disabled', 'false');
      });

      if (gallery.dataset.personalizationPreviewPaused !== 'true') return;

      const preview = gallery.querySelector('.personalize-pilot-main-preview');
      if (preview && !preview.hidden) preview.hidden = true;
      if (preview?.classList.contains('is-active')) preview.classList.remove('is-active');
      const reversePreview = gallery.querySelector('.back-engraving__preview--main');
      if (reversePreview && !reversePreview.hidden) reversePreview.hidden = true;
      if (gallery.classList.contains('is-personalizing')) gallery.classList.remove('is-personalizing');

      const image = gallery.querySelector('[data-product-gallery-stage] img');
      const selectedSrc = gallery.dataset.gallerySelectedSrc;
      if (image && selectedSrc && (image.getAttribute('src') !== selectedSrc || image.getAttribute('srcset') !== selectedSrc)) {
        setProductGalleryImageSource(image, selectedSrc, gallery.dataset.gallerySelectedAlt);
      }

      const previewStatus = gallery.querySelector('[data-personalize-pilot-preview-status]');
      if (previewStatus && !previewStatus.hidden) previewStatus.hidden = true;
    };

    keepGalleryBrowsingState();
    const observer = new MutationObserver(keepGalleryBrowsingState);
    observer.observe(gallery, {
      attributes: true,
      attributeFilter: ['aria-disabled', 'class', 'hidden', 'src'],
      subtree: true,
    });
    gallery.dataset.galleryNavigationReady = 'true';
  });
}

function isAcrylicPersonalizationSection(section) {
  const widget = section?.querySelector('[data-personalize-pilot-widget]');
  if (!widget) return false;

  const productIdentity = [
    widget.dataset.productTitle || '',
    widget.dataset.productHandle || '',
    widget.dataset.personalizationSurfaces || '',
  ]
    .join(' ')
    .toLowerCase();

  return (
    productIdentity.includes('acryl') ||
    productIdentity.includes('acrylic-small') ||
    productIdentity.includes('acrylic-long')
  );
}

let personalizationQaAssetMap;

function getPersonalizationQaAssetMap() {
  if (personalizationQaAssetMap) return personalizationQaAssetMap;
  const source = document.querySelector('[data-personalization-qa-asset-map]');
  try {
    const parsed = JSON.parse(source?.textContent || '{}');
    personalizationQaAssetMap = Array.isArray(parsed.replacements)
      ? parsed.replacements
      : [];
  } catch {
    personalizationQaAssetMap = [];
  }
  return personalizationQaAssetMap;
}

function getQaReplacementForIdentity(values) {
  const source = values
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(' ');
  if (!source) return undefined;
  return getPersonalizationQaAssetMap().find((entry) =>
    [entry.match, entry.originalMatch, entry.fileName]
      .filter(Boolean)
      .some((candidate) => source.includes(String(candidate).toLowerCase())),
  );
}

function getQaReplacementForImage(image) {
  const optionButton = image?.closest?.('[data-template-option]');
  const artworkTarget = image?.closest?.(
    '[data-template-artwork-preview="true"]',
  );
  return getQaReplacementForIdentity([
    image?.currentSrc,
    image?.getAttribute?.('src'),
    image?.getAttribute?.('alt'),
    image?.getAttribute?.('title'),
    optionButton?.dataset?.templateValue,
    optionButton?.dataset?.templateLabel,
    optionButton?.dataset?.templateImage,
    optionButton?.dataset?.templateSource,
    artworkTarget?.dataset?.previewImageSlot,
    artworkTarget?.dataset?.mainPreviewImageSlot,
  ]);
}

function getQaReplacementForSection(section) {
  // The canonical app already exposes the stable fixed-template id on its
  // widget. Prefer that identity over image URLs: Shopify can rewrite the
  // finished image to a hashed CDN URL, while data-fixed-template-value stays
  // tied to the selected product design (for example tpl-shopify-...).
  const widget = section?.querySelector?.('[data-personalize-pilot-widget]');
  const widgetReplacement = getQaReplacementForIdentity([
    widget?.dataset?.fixedTemplateValue,
    widget?.dataset?.templateValue,
    widget?.dataset?.finishedImageUrl,
    widget?.dataset?.mainImageUrl,
    widget?.dataset?.productHandle,
    widget?.dataset?.productTitle,
  ]);
  if (widgetReplacement) return widgetReplacement;

  for (const image of section?.querySelectorAll?.(
    '[data-product-gallery] img, [data-personalize-pilot-product-media] img',
  ) || []) {
    const replacement = getQaReplacementForImage(image);
    if (replacement) return replacement;
  }
  return undefined;
}

function getPersonalizationPreviewSlotId(target) {
  return String(
    target?.dataset?.mainPreviewImageSlot ||
      target?.dataset?.previewImageSlot ||
      '',
  );
}

function isPersonalizePilotCanonicalPreviewOwner(widget) {
  return widget?.dataset?.personalizePilotPreviewOwner === 'app';
}

function getPersonalizationQaArtworkLayout(target) {
  const styles = window.getComputedStyle(target);
  const readPercent = (property, fallback) => {
    const value = Number.parseFloat(styles.getPropertyValue(property));
    return Number.isFinite(value) ? value : fallback;
  };

  return {
    x: readPercent('--slot-x', 50),
    y: readPercent('--slot-y', 50),
    width: readPercent('--slot-width', 100),
    height: readPercent('--slot-height', 100),
  };
}

function projectPersonalizationQaNameGeometry(geometry, artworkLayout) {
  return {
    x: artworkLayout.x + ((geometry.x - 50) * artworkLayout.width) / 100,
    y: artworkLayout.y + ((geometry.y - 50) * artworkLayout.height) / 100,
    width: (geometry.width * artworkLayout.width) / 100,
    height: (geometry.height * artworkLayout.height) / 100,
    fontSizeMm: geometry.fontSizeMm,
  };
}

function applyPersonalizationQaNameGeometry(section, artworkTarget, replacement) {
  const widget = section?.querySelector?.('[data-personalize-pilot-widget]');
  if (isPersonalizePilotCanonicalPreviewOwner(widget)) return false;

  const geometry = replacement?.nameGeometry;
  const slotId = getPersonalizationPreviewSlotId(artworkTarget);
  if (!geometry) return false;

  const localValues = {
    x: Number(geometry.x),
    y: Number(geometry.y),
    width: Number(geometry.width),
    height: Number(geometry.height),
    fontSizeMm: Number(geometry.fontSizeMm),
  };
  if (
    !Number.isFinite(localValues.x) ||
    !Number.isFinite(localValues.y) ||
    !Number.isFinite(localValues.width) ||
    !Number.isFinite(localValues.height)
  ) {
    return false;
  }

  // The measured name plate belongs to the transparent artwork canvas. The
  // artwork itself can occupy only part of the photographed product (for
  // example the acrylic plate inside the dark room photo). Projecting the
  // local name frame through the artwork target keeps both layers locked
  // together instead of treating an artwork-local y value as product-image y.
  const artworkLayout = getPersonalizationQaArtworkLayout(artworkTarget);
  const values = projectPersonalizationQaNameGeometry(
    localValues,
    artworkLayout,
  );

  let changed = false;
  section
    .querySelectorAll('[data-main-preview-text-slot], [data-preview-text-slot]')
    .forEach((textTarget) => {
      const textSlotId = String(
        textTarget.dataset.mainPreviewTextSlot ||
          textTarget.dataset.previewTextSlot ||
          '',
      );
      const isExactOwner = slotId && textSlotId === `${slotId}::0`;
      const isSingleGuidedName =
        textSlotId.endsWith('::0') &&
        textTarget.dataset.templateNameEnabled !== 'false';
      if (!isExactOwner && !isSingleGuidedName) return;

      let sourceLayout = {};
      try {
        sourceLayout = JSON.parse(textTarget.dataset.previewSourceLayout || '{}');
      } catch {
        sourceLayout = {};
      }
      const previousFontSizeMm = Number(
        textTarget.dataset.previewFontSizeMm || sourceLayout.fontSizeMm,
      );
      const nextLayout = {
        ...sourceLayout,
        ...values,
        rotation: 0,
        textCurve: 0,
        coordinateSpace: 'surface',
        boundaryShape: 'rectangle',
        align: 'center',
        autoFitText: true,
        allowLineBreaks: false,
        surfaceMode: 'flat',
        surfaceIntensity: 0,
      };

      textTarget.style.setProperty('--slot-x', `${values.x}%`, 'important');
      textTarget.style.setProperty('--slot-y', `${values.y}%`, 'important');
      textTarget.style.setProperty('--slot-width', `${values.width}%`, 'important');
      textTarget.style.setProperty('--slot-height', `${values.height}%`, 'important');
      textTarget.style.setProperty('--slot-rotation', '0deg', 'important');
      textTarget.style.setProperty('--slot-align', 'center', 'important');
      if (
        Number.isFinite(values.fontSizeMm) &&
        values.fontSizeMm > 0 &&
        Number.isFinite(previousFontSizeMm) &&
        previousFontSizeMm > 0
      ) {
        const currentFontSize = Number.parseFloat(
          window.getComputedStyle(textTarget).fontSize,
        );
        if (Number.isFinite(currentFontSize) && currentFontSize > 0) {
          textTarget.style.setProperty(
            '--slot-size',
            `${(currentFontSize * (values.fontSizeMm / previousFontSizeMm)).toFixed(3)}px`,
            'important',
          );
        }
        textTarget.dataset.previewFontSizeMm = String(values.fontSizeMm);
      }
      textTarget.dataset.previewSourceLayout = JSON.stringify(nextLayout);
      textTarget.dataset.previewResolvedLayout = JSON.stringify(nextLayout);
      textTarget.dataset.qaPersonalizationNameGeometry = replacement.fileName;
      textTarget.dataset.qaPersonalizationNameProjection = JSON.stringify({
        artwork: artworkLayout,
        local: localValues,
        projected: values,
      });
      changed = true;
    });
  return changed;
}

function applyPersonalizationQaOptionMode(section, optionButton, replacement) {
  if (replacement?.nameEnabled !== false || !optionButton) return false;

  optionButton.dataset.templateNameEnabled = 'false';
  optionButton.dataset.templateTextFields = '[]';
  optionButton.dataset.templateUnpersonalizedImage = replacement.url || '';

  if (optionButton.getAttribute('aria-pressed') !== 'true') return true;
  const slotId = optionButton.dataset.templateOption || '';
  if (!slotId) return true;

  const control = (attribute) =>
    Array.from(section.querySelectorAll(`[${attribute}]`)).find(
      (candidate) => candidate.getAttribute(attribute) === slotId,
    );
  const labelInput = control('data-template-label-input');
  const previewInput = control('data-template-preview-input');
  const sourceInput = control('data-template-source-input');
  const unpersonalizedInput = control('data-template-unpersonalized-input');
  if (labelInput && replacement.labelOverride) {
    labelInput.value = replacement.labelOverride;
  }
  if (previewInput) previewInput.value = replacement.url || '';
  if (sourceInput) sourceInput.value = replacement.url || '';
  if (unpersonalizedInput) unpersonalizedInput.value = replacement.url || '';

  section.querySelectorAll('[data-template-name-input]').forEach((input) => {
    if (input.dataset.templateOwnerSlot !== slotId) return;
    input.value = '';
    input.disabled = true;
    input.required = false;
    const wrapper = input.closest('[data-template-text-wrapper]');
    if (wrapper) wrapper.hidden = true;
  });
  section
    .querySelectorAll('[data-template-owned-text="true"]')
    .forEach((target) => {
      const previewSlot = target.dataset.previewSlot || '';
      if (!previewSlot.startsWith(`${slotId}::`)) return;
      target.textContent = '';
      target.hidden = true;
      target.dataset.templateNameEnabled = 'false';
    });
  section.dataset.qaFixedNoNameChoice = replacement.fileName;
  return true;
}

function applyPersonalizationQaAssetReplacements(section) {
  let changed = false;
  // Fixed products can use hashed Shopify file URLs in the canonical app, so
  // their overlay image has no stable filename to match. The gallery alt text
  // still carries the protected tpl-shopify id; resolve it once for the whole
  // product section and use it only when the individual overlay has no match.
  const sectionReplacement = getQaReplacementForSection(section);
  section
    .querySelectorAll(
      '[data-template-artwork-preview="true"] img, [data-template-option] img',
    )
    .forEach((image) => {
      const replacement =
        getQaReplacementForImage(image) || sectionReplacement;
      if (!replacement?.url) return;
      const target = image.closest('[data-template-artwork-preview="true"]');
      if (target) {
        target.dataset.qaPersonalizationKind = replacement.kind;
        target.dataset.qaPersonalizationAsset = replacement.fileName;
        if (applyPersonalizationQaNameGeometry(section, target, replacement)) {
          changed = true;
        }
      }
      const optionButton = image.closest('[data-template-option]');
      if (optionButton) {
        optionButton.dataset.templateImage = replacement.url;
        optionButton.dataset.templateSource = replacement.url;
        optionButton.dataset.qaPersonalizationAsset = replacement.fileName;
        if (
          applyPersonalizationQaOptionMode(
            section,
            optionButton,
            replacement,
          )
        ) {
          changed = true;
        }
        if (replacement.labelOverride) {
          optionButton.dataset.templateLabel = replacement.labelOverride;
          optionButton.title = replacement.labelOverride;
          const visibleLabel = optionButton.querySelector(':scope > small');
          if (visibleLabel && visibleLabel.textContent !== replacement.labelOverride) {
            visibleLabel.textContent = replacement.labelOverride;
          }
        }
      }
      const current = image.getAttribute('src') || '';
      if (current === replacement.url) return;
      image.setAttribute('src', replacement.url);
      image.removeAttribute('srcset');
      changed = true;
    });
  if (changed) section.dataset.qaPersonalizationAssets = 'true';
}

function activatePersonalizationQaAssetReplacements(root = document) {
  root.querySelectorAll('[data-product-section]').forEach((section) => {
    applyPersonalizationQaAssetReplacements(section);
    if (section.dataset.qaPersonalizationAssetsReady === 'true') return;
    let replacementFrame = 0;
    const scheduleReplacement = () => {
      window.cancelAnimationFrame(replacementFrame);
      replacementFrame = window.requestAnimationFrame(() => {
        applyPersonalizationQaAssetReplacements(section);
        // The app creates its large mobile live-preview host after the theme
        // booted. Retry parity activation from the same section observer that
        // already sees those inserted nodes; otherwise that one late preview
        // can keep an older asset while the gallery and sticky preview are
        // correct.
        activateFixedArtworkProductImageParity(section);
        activateMobilePersonalizationPreviewParity(section);
      });
    };
    const observer = new MutationObserver(scheduleReplacement);
    observer.observe(section, {
      attributes: true,
      attributeFilter: ['src', 'srcset'],
      childList: true,
      subtree: true,
    });
    section.addEventListener('change', scheduleReplacement, true);
    section.addEventListener('click', scheduleReplacement, true);
    section.dataset.qaPersonalizationAssetsReady = 'true';
  });
}

function activateAcrylicArtworkSafety(root = document) {
  root.querySelectorAll('[data-product-section]').forEach((section) => {
    const widget = section.querySelector('[data-personalize-pilot-widget]');
    if (isPersonalizePilotCanonicalPreviewOwner(widget)) {
      delete section.dataset.acrylicPreviewSafety;
      return;
    }
    if (!isAcrylicPersonalizationSection(section)) return;
    section.dataset.acrylicPreviewSafety = 'true';
  });
}

function normalizePersonalizationImageUrl(value) {
  const source = String(value || '').trim();
  if (!source) return '';

  try {
    const url = new URL(source, window.location.href);
    url.search = '';
    url.hash = '';
    return url.href.toLowerCase();
  } catch {
    return source.split('?')[0].split('#')[0].toLowerCase();
  }
}

function getFixedArtworkImageContract(widget) {
  const configuredMode = String(
    widget?.dataset.fixedArtworkBaseMode || 'blank-overlay',
  ).trim().toLowerCase();
  const fixedTemplateValue = String(widget?.dataset.fixedTemplateValue || '').trim();
  const finishedImageUrl = String(
    widget?.dataset.finishedImageUrl || widget?.dataset.mainImageUrl || '',
  ).trim();
  const blankImageUrl = String(widget?.dataset.blankImageUrl || '').trim();
  const usesDistinctFinishedImage =
    normalizePersonalizationImageUrl(finishedImageUrl) !==
    normalizePersonalizationImageUrl(blankImageUrl);

  if (!fixedTemplateValue || !finishedImageUrl || !blankImageUrl || !usesDistinctFinishedImage) {
    return undefined;
  }

  return configuredMode === 'finished'
    ? {
        baseImageUrl: finishedImageUrl,
        mode: 'finished-fixed-base',
        suppressesOverlayArtwork: true,
      }
    : {
        baseImageUrl: blankImageUrl,
        mode: 'blank-fixed-overlay',
        suppressesOverlayArtwork: false,
      };
}

function activateFixedArtworkProductImageParity(root = document) {
  root.querySelectorAll('[data-product-section]').forEach((section) => {
    if (section.dataset.fixedArtworkImageParityReady === 'true') return;

    const stageMedia = section.querySelector(
      '[data-product-gallery] [data-product-gallery-stage]',
    );
    const widget = section.querySelector('[data-personalize-pilot-widget]');
    if (isPersonalizePilotCanonicalPreviewOwner(widget)) return;
    if (!stageMedia || !widget || !getFixedArtworkImageContract(widget)) return;

    let parityFrame = 0;
    const enforceParity = () => {
      window.cancelAnimationFrame(parityFrame);
      parityFrame = window.requestAnimationFrame(() => {
        const contract = getFixedArtworkImageContract(widget);
        const image = stageMedia.querySelector('img');
        const templateArtwork = stageMedia.querySelector(
          '.personalize-pilot-main-preview [data-template-artwork-preview="true"]',
        );
        if (!contract || !image || !templateArtwork) return;

        const currentSource = image.getAttribute('src') || image.currentSrc || '';
        if (
          normalizePersonalizationImageUrl(currentSource) !==
          normalizePersonalizationImageUrl(contract.baseImageUrl)
        ) {
          setProductGalleryImageSource(
            image,
            contract.baseImageUrl,
            image.dataset.personalizePilotOriginalAlt || image.alt,
          );
        }

        // The default contract deliberately keeps the certified blank and the
        // current transparent artwork together. A finished image may replace
        // that pair only after the configuration explicitly certifies it as
        // the same artwork version; otherwise an older sales image could undo
        // a newly approved motif when the customer starts personalizing.
        stageMedia
          .querySelectorAll(
            '.personalize-pilot-main-preview [data-template-artwork-preview="true"]',
          )
          .forEach((artwork) => {
            if (contract.suppressesOverlayArtwork) {
              artwork.hidden = true;
              artwork.dataset.fixedArtworkBaseDuplicate = 'suppressed';
            } else if (artwork.dataset.fixedArtworkBaseDuplicate === 'suppressed') {
              artwork.hidden = false;
              delete artwork.dataset.fixedArtworkBaseDuplicate;
            }
          });

        image.style.setProperty('object-fit', 'contain', 'important');
        image.style.setProperty('object-position', 'center', 'important');
        stageMedia.dataset.personalizePilotImageContract = contract.mode;
        section.dataset.personalizePilotImageContract = contract.mode;
      });
    };

    const observer = new MutationObserver(enforceParity);
    observer.observe(section, {
      attributes: true,
      attributeFilter: [
        'data-finished-image-url',
        'data-main-image-url',
        'data-fixed-template-value',
        'hidden',
        'src',
        'srcset',
      ],
      childList: true,
      subtree: true,
    });
    section.addEventListener('input', enforceParity, true);
    section.addEventListener('change', enforceParity, true);
    section.addEventListener('click', enforceParity, true);
    enforceParity();
    section.dataset.fixedArtworkImageParityReady = 'true';
  });
}

function getPersonalizationFieldSemanticKeys(value) {
  const normalized = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const keys = new Set();
  if (/\b(?:name|vorname|nachname|familienname|wunschname|wunschtext|gravurtext)\b/.test(normalized)) {
    keys.add('person-name');
  }
  if (/\b(?:datum|date|jahr|jahreszahl)\b/.test(normalized)) {
    keys.add('date');
  }
  if (keys.size === 0 && normalized) keys.add(`label:${normalized}`);
  return keys;
}

function getTemplateButtonSemanticKeys(button) {
  let fields = [];
  try {
    const parsed = JSON.parse(button.dataset.templateTextFields || '[]');
    fields = Array.isArray(parsed) ? parsed : [];
  } catch {
    fields = [];
  }
  if (fields.length === 0 && button.dataset.templateNameEnabled !== 'false') {
    fields = [{ label: button.dataset.templateLegacyLabel || 'Dein Name' }];
  }
  const keys = new Set();
  fields.forEach((field) => {
    getPersonalizationFieldSemanticKeys(field?.label).forEach((key) => keys.add(key));
  });
  return keys;
}

function enforceTemplateOwnedFieldDeduplication(widget) {
  const section = widget.closest('[data-product-section]') || widget;
  const ownedKeys = new Set();

  widget.querySelectorAll('[data-template-text-fields-container]').forEach((container) => {
    const ownerId = container.dataset.templateTextFieldsContainer || '';
    const buttons = Array.from(widget.querySelectorAll('[data-template-option]')).filter(
      (button) =>
        button.dataset.templateOption === ownerId &&
        button.dataset.templateNameEnabled !== 'false',
    );
    if (buttons.length === 0) return;

    const keysByOption = buttons.map(getTemplateButtonSemanticKeys);
    const commonKeys = [...(keysByOption[0] || [])].filter((key) =>
      keysByOption.every((keys) => keys.has(key)),
    );
    const surfaceId =
      buttons[0]?.dataset.templateSurfaceId ||
      container.closest('[data-personalization-surface]')?.dataset.personalizationSurface ||
      'front';
    commonKeys.forEach((key) => ownedKeys.add(`${surfaceId}::${key}`));
  });

  widget
    .querySelectorAll(
      '.personalize-pilot-widget__field[data-personalization-slot-container]',
    )
    .forEach((wrapper) => {
      if (wrapper.closest('[data-template-text-fields-container]')) return;
      const input = wrapper.querySelector('[data-personalization-slot-id]');
      if (!input) return;
      const label =
        input.getAttribute('name') ||
        wrapper.querySelector(':scope > span')?.textContent ||
        '';
      const surfaceId = wrapper.dataset.personalizationSurface || 'front';
      const duplicatesTemplateField = [...getPersonalizationFieldSemanticKeys(label)].some(
        (key) => ownedKeys.has(`${surfaceId}::${key}`),
      );
      const slotId = input.dataset.personalizationSlotId || '';

      if (duplicatesTemplateField) {
        const activeTemplateInput = Array.from(
          widget.querySelectorAll('[data-template-name-input]'),
        ).find((candidate) => !candidate.disabled);
        if (activeTemplateInput && !activeTemplateInput.value && input.value) {
          activeTemplateInput.value = input.value;
        }
        wrapper.hidden = true;
        wrapper.dataset.templateOwnedDuplicate = 'suppressed';
        input.disabled = true;
        input.required = false;
        input.setAttribute('aria-hidden', 'true');
        section
          .querySelectorAll('[data-preview-text-slot], [data-main-preview-text-slot]')
          .forEach((target) => {
            const targetSlot =
              target.dataset.previewTextSlot || target.dataset.mainPreviewTextSlot || '';
            if (targetSlot !== slotId) return;
            target.hidden = true;
            target.dataset.templateOwnedDuplicate = 'suppressed';
          });
        return;
      }

      if (wrapper.dataset.templateOwnedDuplicate === 'suppressed') {
        wrapper.hidden = false;
        delete wrapper.dataset.templateOwnedDuplicate;
        input.disabled = false;
        input.removeAttribute('aria-hidden');
      }
      section
        .querySelectorAll('[data-template-owned-duplicate="suppressed"]')
        .forEach((target) => {
          const targetSlot =
            target.dataset.previewTextSlot || target.dataset.mainPreviewTextSlot || '';
          if (targetSlot !== slotId) return;
          target.hidden = false;
          delete target.dataset.templateOwnedDuplicate;
        });
    });

  // Template fields own their individual requiredness. A required motif
  // wrapper must not turn an explicitly optional year into a required input.
  widget.querySelectorAll('[data-template-name-input]').forEach((input) => {
    if (!/^(money_holder|acrylic_sign)-(jugendweihe-die-welt-wartet|zwillinge-doppeltes-glueck|genesung-gute-besserung|ehrenamt-mit-herz|auslandsjahr-ab-ins-abenteuer|richtfest-unser-zuhause-waechst)-motif$/.test(input.dataset.templateOwnerSlot || '')) return;
    const required = !input.disabled && !input.closest('[hidden]') &&
      input.dataset.templateNameRequired === 'true';
    const wrapper = input.closest('[data-template-text-wrapper]');
    // The app prepares fields synchronously during submission. Individual
    // wrappers preserve the same rules during that preparation as in the UI.
    if (wrapper && !input.disabled && !input.closest('[hidden]')) {
      wrapper.dataset.personalizeField = '';
      wrapper.dataset.fieldRequired = input.dataset.templateNameRequired;
      wrapper.dataset.illustratedGiftField = 'true';
    } else if (wrapper?.dataset.illustratedGiftField === 'true') {
      delete wrapper.dataset.personalizeField;
      delete wrapper.dataset.fieldRequired;
      delete wrapper.dataset.illustratedGiftField;
    }
    if (input.required !== required) input.required = required;
  });
  widget.dataset.templateOwnedFieldContract = 'one-visible-input-per-semantic-field';
}

function activateTemplateOwnedFieldDeduplication(root = document) {
  root.querySelectorAll('[data-personalize-pilot-widget]').forEach((widget) => {
    if (widget.dataset.templateOwnedFieldDeduplicationReady === 'true') return;
    let frame = 0;
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() =>
        enforceTemplateOwnedFieldDeduplication(widget),
      );
    };
    const observer = new MutationObserver(schedule);
    observer.observe(widget, {
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'aria-pressed', 'required', 'data-template-name-required'],
      childList: true,
      subtree: true,
    });
    widget.addEventListener('input', schedule, true);
    widget.addEventListener('change', schedule, true);
    widget.addEventListener('click', schedule, true);
    schedule();
    widget.dataset.templateOwnedFieldDeduplicationReady = 'true';
  });
}

function syncPersonalizationFollowerOverlayGeometry(stageMedia, clone) {
  const stageRect = stageMedia.getBoundingClientRect();
  const sourceOverlays = Array.from(
    stageMedia.querySelectorAll('.personalize-pilot-main-preview'),
  );
  const cloneOverlays = Array.from(
    clone.querySelectorAll('.personalize-pilot-main-preview'),
  );

  if (stageRect.width <= 1 || stageRect.height <= 1) return;

  sourceOverlays.forEach((sourceOverlay, index) => {
    const cloneOverlay = cloneOverlays[index];
    const overlayRect = sourceOverlay.getBoundingClientRect();
    if (!cloneOverlay || overlayRect.width <= 1 || overlayRect.height <= 1) return;

    const left = ((overlayRect.left - stageRect.left) / stageRect.width) * 100;
    const top = ((overlayRect.top - stageRect.top) / stageRect.height) * 100;
    const width = (overlayRect.width / stageRect.width) * 100;
    const height = (overlayRect.height / stageRect.height) * 100;

    // The source overlay is measured once against the real product image and
    // converted to relative coordinates. Both follower previews then retain
    // precisely that rectangle instead of stretching it to their containers.
    cloneOverlay.style.inset = 'auto';
    cloneOverlay.style.left = `${left.toFixed(4)}%`;
    cloneOverlay.style.top = `${top.toFixed(4)}%`;
    cloneOverlay.style.right = 'auto';
    cloneOverlay.style.bottom = 'auto';
    cloneOverlay.style.width = `${width.toFixed(4)}%`;
    cloneOverlay.style.height = `${height.toFixed(4)}%`;
    cloneOverlay.style.transform = 'none';
    cloneOverlay.dataset.personalizePilotCloneGeometry = [
      left,
      top,
      width,
      height,
    ]
      .map((value) => value.toFixed(4))
      .join(',');
  });
}

function createPersonalizationFollowerMedia(stageMedia) {
  const clone = stageMedia.cloneNode(true);
  const sourceCanvases = Array.from(stageMedia.querySelectorAll('canvas'));
  const cloneCanvases = Array.from(clone.querySelectorAll('canvas'));
  const stageRect = stageMedia.getBoundingClientRect();

  clone.removeAttribute('id');
  clone.removeAttribute('data-product-gallery-stage');
  clone.removeAttribute('data-personalize-pilot-product-media');
  clone.classList.add('product-gallery-follower__media');
  const productSection = stageMedia.closest('[data-product-section]');
  const previewWidget = productSection?.querySelector(
    '[data-personalize-pilot-widget]',
  );
  if (
    isAcrylicPersonalizationSection(productSection) &&
    !isPersonalizePilotCanonicalPreviewOwner(previewWidget)
  ) {
    clone.dataset.acrylicPreviewSafety = 'true';
  }
  clone.style.aspectRatio =
    stageRect.width > 1 && stageRect.height > 1
      ? `${stageRect.width} / ${stageRect.height}`
      : '1 / 1';
  clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
  clone
    .querySelectorAll(
      'button, input, select, textarea, [hidden], [data-personalize-pilot-preview-status], .personalize-pilot-main-preview__design-area, .personalize-pilot-widget__freeform-handle',
    )
    .forEach((element) => element.remove());
  clone
    .querySelectorAll('.is-customer-editable, .is-selected, .is-dragging')
    .forEach((element) => {
      element.classList.remove('is-customer-editable', 'is-selected', 'is-dragging');
      element.removeAttribute('tabindex');
    });
  clone.querySelectorAll('.personalize-pilot-main-preview').forEach((overlay) => {
    overlay.hidden = false;
    overlay.classList.remove('is-customer-editing');
  });
  syncPersonalizationFollowerOverlayGeometry(stageMedia, clone);

  sourceCanvases.forEach((sourceCanvas, index) => {
    const cloneCanvas = cloneCanvases[index];
    if (!cloneCanvas) return;

    try {
      const canvasImage = document.createElement('img');
      canvasImage.src = sourceCanvas.toDataURL('image/png');
      canvasImage.alt = '';
      canvasImage.className = cloneCanvas.className;
      canvasImage.setAttribute('aria-hidden', 'true');
      cloneCanvas.replaceWith(canvasImage);
    } catch {
      cloneCanvas.width = sourceCanvas.width;
      cloneCanvas.height = sourceCanvas.height;
      cloneCanvas.getContext('2d')?.drawImage(sourceCanvas, 0, 0);
    }
  });

  return clone;
}

function refitPersonalizationFollowerText(stageMedia, followerViewport) {
  const sourceTexts = Array.from(
    stageMedia.querySelectorAll(
      '[data-main-preview-text-slot], [data-preview-text-slot]',
    ),
  );

  followerViewport
    .querySelectorAll(
      '[data-main-preview-text-slot], [data-preview-text-slot]',
    )
    .forEach((textElement) => {
      const text = textElement.textContent?.trim() || '';
      if (!text || textElement.hidden) return;

      const slotId =
        textElement.dataset.mainPreviewTextSlot ||
        textElement.dataset.previewTextSlot ||
        '';
      const sourceText = sourceTexts.find(
        (candidate) =>
          (candidate.dataset.mainPreviewTextSlot ||
            candidate.dataset.previewTextSlot ||
            '') === slotId,
      );
      if (!sourceText) return;

      const sourceStage =
        sourceText.closest('.personalize-pilot-main-preview') || stageMedia;
      const followerStage =
        textElement.closest('.personalize-pilot-main-preview') || followerViewport;
      const sourceStageRect = sourceStage.getBoundingClientRect();
      const followerStageRect = followerStage.getBoundingClientRect();
      const scale = Math.min(
        followerStageRect.width / Math.max(sourceStageRect.width, 1),
        followerStageRect.height / Math.max(sourceStageRect.height, 1),
      );
      const sourceStyles = window.getComputedStyle(sourceText);
      const fontSize = Number(
        (Number.parseFloat(sourceStyles.fontSize) * scale).toFixed(3),
      );
      if (!Number.isFinite(fontSize) || fontSize <= 0) return;
      const sourceLineHeight = Number.parseFloat(sourceStyles.lineHeight);
      const lineHeight = Number.isFinite(sourceLineHeight) && sourceLineHeight > 0
        ? Number((sourceLineHeight * scale).toFixed(3))
        : fontSize;

      textElement.style.fontFamily = sourceStyles.fontFamily;
      textElement.style.fontWeight = sourceStyles.fontWeight;
      textElement.style.fontStyle = sourceStyles.fontStyle;
      textElement.style.letterSpacing = sourceStyles.letterSpacing;
      textElement.style.lineHeight = `${lineHeight}px`;
      textElement.style.color = sourceStyles.color;
      textElement.style.textShadow = sourceStyles.textShadow;
      textElement.style.filter = sourceStyles.filter;
      textElement.style.mixBlendMode = sourceStyles.mixBlendMode;
      textElement.style.opacity = sourceStyles.opacity;
      textElement.style.webkitTextStroke = sourceStyles.webkitTextStroke;
      textElement.style.setProperty('--slot-size', `${fontSize}px`);
      textElement.style.fontSize = `${fontSize}px`;
    });
}

function getPersonalizationFollowerSignature(stageMedia) {
  const rect = stageMedia.getBoundingClientRect();
  const image = stageMedia.querySelector('img');
  return [
    stageMedia.innerHTML,
    stageMedia.dataset.personalizePilotPreviewMode || '',
    image?.currentSrc || image?.getAttribute('src') || '',
    Math.round(rect.width * 100) / 100,
    Math.round(rect.height * 100) / 100,
    document.fonts?.status || 'unsupported',
  ].join('\u241f');
}

function activateStickyPersonalizationPreview(root = document) {
  root.querySelectorAll('[data-product-section]').forEach((section) => {
    if (section.dataset.stickyPersonalizationReady === 'true') return;

    const gallery = section.querySelector('[data-product-gallery]');
    const stageMedia = gallery?.querySelector('[data-product-gallery-stage]');
    const widget = section.querySelector('[data-personalize-pilot-widget]');
    if (!gallery || !stageMedia || !widget) return;

    const mobileViewport = window.matchMedia('(max-width: 899px)');
    const follower = document.createElement('div');
    const followerViewport = document.createElement('div');
    const followerLabel = document.createElement('span');
    let renderFrame = 0;
    let followerSourceSignature = '';

    follower.className = 'product-gallery-follower';
    follower.setAttribute('aria-hidden', 'true');
    follower.dataset.productGalleryFollower = 'true';
    followerLabel.className = 'product-gallery-follower__label';
    followerLabel.textContent = 'Deine Live-Vorschau';
    followerViewport.className = 'product-gallery-follower__viewport';
    follower.append(followerLabel, followerViewport);
    document.body.appendChild(follower);

    const renderFollower = () => {
      window.cancelAnimationFrame(renderFrame);
      renderFrame = window.requestAnimationFrame(() => {
        const nextSignature = getPersonalizationFollowerSignature(stageMedia);
        if (
          nextSignature === followerSourceSignature &&
          followerViewport.firstElementChild
        ) {
          if (
            isPersonalizePilotCanonicalPreviewOwner(widget) &&
            typeof widget.personalizePilotSyncFollowers === 'function'
          ) {
            widget.personalizePilotSyncFollowers();
          } else {
            refitPersonalizationFollowerText(stageMedia, followerViewport);
          }
          return;
        }
        const nextFollowerMedia = createPersonalizationFollowerMedia(stageMedia);
        const stageRect = stageMedia.getBoundingClientRect();
        // The clone initially contains the large gallery font size. Keep that
        // intermediate state invisible until the compact follower has been
        // measured and fitted, otherwise names visibly jump on every refresh.
        nextFollowerMedia.style.visibility = 'hidden';
        followerSourceSignature = nextSignature;
        followerViewport.style.aspectRatio =
          stageRect.width > 1 && stageRect.height > 1
            ? `${stageRect.width} / ${stageRect.height}`
            : '1 / 1';
        followerViewport.replaceChildren(nextFollowerMedia);
        window.requestAnimationFrame(() => {
          if (
            isPersonalizePilotCanonicalPreviewOwner(widget) &&
            typeof widget.personalizePilotSyncFollowers === 'function'
          ) {
            widget.personalizePilotSyncFollowers();
          } else {
            refitPersonalizationFollowerText(stageMedia, followerViewport);
          }
          nextFollowerMedia.style.visibility = '';
        });
      });
    };

    const updateFollowerVisibility = () => {
      if (!mobileViewport.matches) {
        follower.classList.remove('is-visible');
        return;
      }

      const galleryRect = gallery.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const personalizationActive =
        gallery.classList.contains('is-personalizing') ||
        widget.classList.contains('is-personalizing');
      const shouldFollow =
        personalizationActive &&
        galleryRect.bottom < 92 &&
        sectionRect.bottom > 220 &&
        sectionRect.top < window.innerHeight;

      const wasVisible = follower.classList.contains('is-visible');
      follower.classList.toggle('is-visible', shouldFollow);
      if (shouldFollow && !wasVisible) renderFollower();
    };

    const scheduleFollowerRefresh = () => {
      if (follower.classList.contains('is-visible')) renderFollower();
      updateFollowerVisibility();
    };

    const previewObserver = new MutationObserver(scheduleFollowerRefresh);
    previewObserver.observe(stageMedia, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });

    window.addEventListener('scroll', updateFollowerVisibility, { passive: true });
    window.addEventListener('resize', scheduleFollowerRefresh, { passive: true });
    section.addEventListener('input', scheduleFollowerRefresh, true);
    section.addEventListener('change', scheduleFollowerRefresh, true);
    section.addEventListener('click', scheduleFollowerRefresh, true);
    mobileViewport.addEventListener?.('change', updateFollowerVisibility);

    renderFollower();
    updateFollowerVisibility();
    section.dataset.stickyPersonalizationReady = 'true';
  });
}

function activateMobilePersonalizationPreviewParity(root = document) {
  root.querySelectorAll('[data-product-section]').forEach((section) => {
    if (section.dataset.mobilePersonalizationPreviewParityReady === 'true') return;

    const stageMedia = section.querySelector(
      '[data-product-gallery] [data-product-gallery-stage]',
    );
    const widget = section.querySelector('[data-personalize-pilot-widget]');
    if (isPersonalizePilotCanonicalPreviewOwner(widget)) return;
    const previewHost = widget?.querySelector('[data-mobile-live-preview-content]');
    if (!stageMedia || !previewHost) return;

    let parityFrame = 0;
    const renderParityPreview = () => {
      window.cancelAnimationFrame(parityFrame);
      // PersonalizePilot refreshes its own clone in the first animation frame.
      // Render the shared, verified theme clone one frame later so the large
      // inline preview cannot fall back to a narrower overlay or lose a text
      // slot that became visible only after the customer started typing.
      parityFrame = window.requestAnimationFrame(() => {
        parityFrame = window.requestAnimationFrame(() => {
          const clone = createPersonalizationFollowerMedia(stageMedia);
          const stageRect = stageMedia.getBoundingClientRect();

          clone.classList.add('personalize-pilot-mobile-preview__product');
          clone.dataset.themePreviewParity = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.style.visibility = 'hidden';
          previewHost.replaceChildren(clone);
          previewHost.style.setProperty(
            '--personalize-pilot-mobile-preview-ratio',
            String(stageRect.width / Math.max(stageRect.height, 1)),
          );
          previewHost.style.aspectRatio =
            stageRect.width > 0 && stageRect.height > 0
              ? `${stageRect.width} / ${stageRect.height}`
              : '1 / 1';

          window.requestAnimationFrame(() => {
            refitPersonalizationFollowerText(stageMedia, previewHost);
            clone.style.visibility = '';
          });
        });
      });
    };

    const hostObserver = new MutationObserver(() => {
      if (previewHost.firstElementChild?.dataset.themePreviewParity !== 'true') {
        renderParityPreview();
      }
    });
    hostObserver.observe(previewHost, { childList: true });

    section.addEventListener('input', renderParityPreview, true);
    section.addEventListener('change', renderParityPreview, true);
    window.addEventListener('resize', renderParityPreview, { passive: true });
    document.fonts?.ready.then(renderParityPreview);

    renderParityPreview();
    section.dataset.mobilePersonalizationPreviewParityReady = 'true';
  });
}

function resumePersonalizationPreview(event) {
  const widget = event.target.closest?.('[data-personalize-pilot-widget]');
  const baseEditor = ['input', 'change'].includes(event.type) ? event.target.closest?.('wood-base-engraving-option') : null;
  let purchaseControl = null;
  if (event.type === 'click') purchaseControl = event.target.closest?.('[data-add-to-cart]');
  if (event.type === 'submit') purchaseControl = event.target.closest?.('form');
  const productSection = widget?.closest('[data-product-section]') || baseEditor?.closest('[data-product-section]') || purchaseControl?.closest('[data-product-section]');
  const gallery = productSection?.querySelector('[data-product-gallery]');
  if (!gallery) return;
  if (baseEditor && gallery.dataset.supplementalProductGallery === 'true') {
    const first = gallery.querySelector('[data-product-gallery-thumb]');
    const image = gallery.querySelector('[data-product-gallery-stage] .product-gallery__stage-image');
    if (image && first?.dataset.galleryImageSrc) setProductGalleryImageSource(image, first.dataset.galleryImageSrc, first.dataset.galleryImageAlt);
    delete gallery.dataset.personalizationPreviewPaused;
    delete gallery.dataset.gallerySelectedSrc;
    delete gallery.dataset.gallerySelectedAlt;
    gallery.querySelectorAll('[data-product-gallery-thumb]').forEach((thumb, index) => {
      thumb.classList.toggle('is-active', index === 0);
      thumb.setAttribute('aria-pressed', String(index === 0));
    });
    const counter = gallery.querySelector('[data-product-gallery-counter]');
    if (counter) counter.textContent = `Bild 1 von ${gallery.querySelectorAll('[data-product-gallery-thumb]').length}`;
    const nameInput = productSection.querySelector('[data-personalize-pilot-widget] input[type="text"]:not([disabled]), [data-personalize-pilot-widget] textarea:not([disabled])');
    nameInput?.dispatchEvent(new Event('input', { bubbles: true }));
    return;
  }
  if (widget) delete gallery.dataset.personalizationPreviewPaused;
  if (!purchaseControl || gallery.dataset.supplementalProductGallery !== 'true' || !productSection.querySelector('[data-personalize-pilot-widget]')) return;

  delete gallery.dataset.personalizationPreviewPaused;
  delete gallery.dataset.gallerySelectedSrc;
  delete gallery.dataset.gallerySelectedAlt;
  const thumbnails = Array.from(gallery.querySelectorAll('[data-product-gallery-thumb]'));
  thumbnails.forEach((thumbnail, index) => {
    thumbnail.classList.toggle('is-active', index === 0);
    thumbnail.setAttribute('aria-pressed', String(index === 0));
  });
  const counter = gallery.querySelector('[data-product-gallery-counter]');
  if (counter && thumbnails.length) counter.textContent = `Bild 1 von ${thumbnails.length}`;
  const nameInput = productSection.querySelector('[data-personalize-pilot-widget] input[type="text"]:not([disabled]), [data-personalize-pilot-widget] textarea:not([disabled])');
  if (nameInput?.value) nameInput.dispatchEvent(new Event('input', { bubbles: true }));
}

function activateBottleOpenerFixedPicker(root = document) {
  root.querySelectorAll('[data-bottle-opener-fixed-picker]').forEach((picker) => {
    if (picker.dataset.fixedPickerReady === 'true') return;
    const section = picker.closest('[data-product-section]');
    const widget = section?.querySelector('[data-personalize-pilot-widget]');

    const modeInputs = Array.from(
      widget?.querySelectorAll('[data-personalization-choice-input]') || [],
    );
    const fixedOptions = Array.from(
      picker.querySelectorAll('[data-bottle-opener-fixed-option]'),
    );
    const fileInputs = Array.from(
      picker.querySelectorAll('[data-bottle-opener-fixed-file]'),
    );
    const preview = picker.querySelector('[data-bottle-opener-fixed-preview]');
    const previewArtwork = picker.querySelector(
      '[data-bottle-opener-fixed-preview-art]',
    );
    const status = picker.querySelector('[data-bottle-opener-fixed-status]');

    const clearFixedChoice = () => {
      fixedOptions.forEach((option) => {
        option.checked = false;
      });
      fileInputs.forEach((fileInput) => {
        fileInput.checked = false;
        fileInput.defaultChecked = false;
        fileInput.removeAttribute('checked');
        fileInput.disabled = true;
      });
      if (previewArtwork) {
        previewArtwork.removeAttribute('src');
        previewArtwork.alt = '';
        previewArtwork.hidden = true;
      }
      if (preview) preview.hidden = true;
      if (status) status.textContent = '';
    };

    const syncMode = () => {
      const standardMode = modeInputs.find(
        (input) => input.value === 'standard',
      );
      if (!standardMode) {
        picker.hidden = false;
        return;
      }
      picker.hidden = !standardMode.checked;
      if (!standardMode.checked) clearFixedChoice();
    };

    fixedOptions.forEach((option) => {
      option.addEventListener('change', () => {
        if (!option.checked) return;
        const assetFile = option.dataset.assetFile || '';
        fileInputs.forEach((fileInput) => {
          const selected = fileInput.dataset.assetFile === assetFile;
          fileInput.checked = selected;
          fileInput.defaultChecked = selected;
          fileInput.toggleAttribute('checked', selected);
          fileInput.disabled = !selected;
        });
        if (previewArtwork) {
          previewArtwork.src = option.dataset.assetUrl || '';
          previewArtwork.alt = option.value;
          previewArtwork.hidden = false;
        }
        if (preview) preview.hidden = false;
        if (status) {
          status.textContent = `${picker.dataset.selectedPrefix || ''} ${option.value}`.trim();
        }
      });
    });
    modeInputs.forEach((input) => input.addEventListener('change', syncMode));
    syncMode();
    picker.dataset.fixedPickerReady = 'true';
  });
}

activatePersonalizationQaAssetReplacements();
activateAcrylicArtworkSafety();
activateFixedArtworkProductImageParity();
activateTemplateOwnedFieldDeduplication();
activateProductGalleryNavigation();
activateStickyPersonalizationPreview();
activateMobilePersonalizationPreviewParity();
activateBottleOpenerFixedPicker();
document.addEventListener('shopify:section:load', (event) => {
  activatePersonalizationQaAssetReplacements(event.target);
  activateAcrylicArtworkSafety(event.target);
  activateFixedArtworkProductImageParity(event.target);
  activateTemplateOwnedFieldDeduplication(event.target);
  activateProductGalleryNavigation(event.target);
  activateStickyPersonalizationPreview(event.target);
  activateMobilePersonalizationPreviewParity(event.target);
  activateBottleOpenerFixedPicker(event.target);
});
document.addEventListener('input', resumePersonalizationPreview, true);
document.addEventListener('change', resumePersonalizationPreview, true);
document.addEventListener('click', resumePersonalizationPreview, true);
document.addEventListener('submit', resumePersonalizationPreview, true);

document.addEventListener('click', (event) => {
  const galleryThumb = event.target.closest?.('[data-product-gallery-thumb]');
  if (galleryThumb) {
    event.preventDefault();
    selectProductGalleryImage(galleryThumb);
    return;
  }

  const galleryDirection = event.target.closest?.('[data-product-gallery-direction]');
  if (galleryDirection) {
    event.preventDefault();
    stepProductGallery(galleryDirection);
  }
}, true);

document.addEventListener('click', (event) => {
  const personalizationTrigger = event.target.closest('[data-personalization-target]');
  const studio = document.querySelector('personalization-studio');
  if (personalizationTrigger && studio) {
    event.preventDefault();
    studio.selectProfile(personalizationTrigger.dataset.personalizationTarget);
    studio.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  const zoomTrigger = event.target.closest('[data-media-dialog-open]');
  if (zoomTrigger) {
    const dialog = document.getElementById(zoomTrigger.getAttribute('aria-controls'));
    dialog?.showModal();
  }

  const closeTrigger = event.target.closest('[data-dialog-close]');
  if (closeTrigger) closeTrigger.closest('dialog')?.close();
});

document.addEventListener('change', (event) => {
  const variantSelect = event.target.closest('[data-variant-select]');
  if (variantSelect) updateSelectedVariant(variantSelect);

  const sortSelect = event.target.closest('[data-sort-select]');
  if (sortSelect) sortSelect.form?.submit();
});

const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

if (!mediaQuery.matches && 'IntersectionObserver' in window) {
  const sections = document.querySelectorAll('.shopify-section');
  sections.forEach((section) => section.setAttribute('data-reveal', ''));
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -7% 0px' },
  );
  sections.forEach((section) => observer.observe(section));

  const revealItems = document.querySelectorAll('[data-reveal-item]');
  const itemObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          itemObserver.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -5% 0px', threshold: 0.08 },
  );
  revealItems.forEach((item) => itemObserver.observe(item));
} else {
  document.querySelectorAll('[data-reveal-item]').forEach((item) => item.classList.add('is-visible'));
}

if (!mediaQuery.matches && finePointerQuery.matches) {
  document.querySelectorAll('[data-ambient-scene]').forEach((scene) => {
    let frame;
    const layers = scene.querySelectorAll('[data-scene-layer]');
    const resetLayers = () => {
      cancelAnimationFrame(frame);
      layers.forEach((layer) => {
        layer.style.setProperty('--layer-x', '0px');
        layer.style.setProperty('--layer-y', '0px');
      });
    };
    scene.addEventListener('pointermove', (event) => {
      const rect = scene.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        layers.forEach((layer) => {
          const depth = Number(layer.dataset.sceneLayer || 0.4);
          layer.style.setProperty('--layer-x', `${x * depth * 34}px`);
          layer.style.setProperty('--layer-y', `${y * depth * 24}px`);
        });
      });
    });
    scene.addEventListener('pointerleave', resetLayers);
    scene.addEventListener('pointercancel', resetLayers);
  });
}

const siteHeader = document.querySelector('.site-header');
if (siteHeader) {
  const updateHeader = () => siteHeader.classList.toggle('is-scrolled', window.scrollY > 12);
  updateHeader();
  window.addEventListener('scroll', updateHeader, { passive: true });
}

document.querySelectorAll('[data-recommendations-url]').forEach(async (element) => {
  try {
    const response = await fetch(element.dataset.recommendationsUrl);
    if (!response.ok) return;
    const html = document.createElement('div');
    html.innerHTML = await response.text();
    const recommendations = html.querySelector('[data-product-recommendations]');
    const destination = element.querySelector('[data-product-recommendations]');
    if (destination && recommendations?.querySelector('.product-card')) {
      destination.innerHTML = recommendations.innerHTML;
      destination.querySelectorAll('[data-reveal-item]').forEach((card) => card.classList.add('is-visible'));
    }
  } catch {
    // Recommendations are optional; the core product flow remains available.
  }
});

